import React from 'react';
import { AcompanheRedesCarouselMobile } from './AcompanheRedesCarouselMobile';
import { DevicesIcon } from './DevicesIcon';
import { RedeCard } from './RedeCard';
import { YoutubeDialog } from './YoutubeDialog';
import {
  redesCardHasContent,
  toRedesCards,
  toRedesPerfis,
  type RedesData,
  type YoutubeVideoRef,
} from './helpers';
import { SocialIcon } from 'volto-ministerio-publico-federal/components/Icons/social';
import { MPFLink } from 'volto-ministerio-publico-federal/components/UI';
import { TITULO_FAIXA } from './typography';

interface AcompanheRedesSectionProps {
  data?: RedesData;
  isEditMode?: boolean;
  /** Presente só no editor: o slot do card em que se clicou o lápis. */
  onEditItem?: (slot: number) => void;
}

export const REDES_TITLE = 'Acompanhe nossas redes';

/* O laranja do design mora só como CSS var: a paleta `orange` do Tailwind não
   é redirecionada em `tailwind.config.js` (ver o comentário em
   `tokens/_css-vars.scss`), então `text-orange-500` entregaria outro tom. Daí
   o valor arbitrário. */
const ORANGE = 'text-[color:var(--orange-500)]';

/* Visibilidade da fileira de ícones dos perfis oficiais. Hoje só de `lg` em
   diante; para tirá-la de todas as telas troque por `'hidden'`, e para
   exibi-la sempre, por `'flex'`. */
const PERFIS_VISIBILIDADE = 'hidden lg:flex';

/* Caixa de nível de bloco, no lugar da utility `block` do Tailwind.
   `.block` é também o nome da classe do wrapper de bloco do Volto, que carrega
   `margin-bottom: 1em` global — a utility de display herdava essa margem e
   empurrava o título 16px para baixo da imagem (mais 16 do `<a>` em volta).
   `flex` entrega a mesma caixa de bloco sem colidir com nome de componente.
   Mesma família da colisão `.fixed` do Tailwind com a tabela do Semantic. */
const BLOCO = 'flex';

/**
 * Faixa "Acompanhe nossas redes" (bloco `mpfAcompanheRedes`).
 *
 * Cabeçalho com os perfis oficiais e uma grade de cinco posts: quatro cards
 * pequenos em 2×2 e um card alto de vídeo na terceira coluna. Abaixo de `md`
 * os quatro pequenos viram um carrossel e o card de vídeo fica solto abaixo
 * dele, como manda o design mobile/tablet — mesmo corte de breakpoint da faixa
 * "Iniciativas e Projetos".
 *
 * No site só aparecem os cards com URL; no editor os cinco ficam visíveis,
 * senão não haveria onde clicar para preencher um slot vazio.
 */
export const AcompanheRedesSection: React.FC<AcompanheRedesSectionProps> = ({
  data,
  isEditMode,
  onEditItem,
}) => {
  const [video, setVideo] = React.useState<YoutubeVideoRef | null>(null);

  const cards = React.useMemo(() => {
    const all = toRedesCards(data);
    return isEditMode ? all : all.filter(redesCardHasContent);
  }, [data, isEditMode]);

  const perfis = React.useMemo(() => toRedesPerfis(data), [data]);

  const pequenos = cards.filter((card) => !card.isVideoSlot);
  const grande = cards.find((card) => card.isVideoSlot);

  return (
    /* Mesma moldura das outras faixas: traço, raio 16 e padding 16, com os 16
       entre cabeçalho e grade saindo do `gap`.
       `not-prose` porque esta seção também é usada como BLOCO numa página
       comum, e ali ela cai dentro do `prose` do corpo do conteúdo
       (`base/_editor-content.scss`): o título ganhava `margin-top: 48px` e a
       fileira de ícones virava lista com marcador. Na capa é inócuo — lá não
       há `prose` em volta. */
    <section
      aria-labelledby="capa-redes-titulo"
      className="not-prose flex flex-col gap-4 rounded-2xl border border-gray-200 bg-surface-container-lowest p-4 dark:border-night-850 dark:bg-night-800"
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <DevicesIcon
              className={`h-6 w-6 shrink-0 ${ORANGE} dark:text-white`}
            />
            {/* Título e ícone são fixos — não há campo editável para eles. */}
            <h2
              id="capa-redes-titulo"
              className={`${TITULO_FAIXA} text-blue-800 dark:text-white`}
            >
              {REDES_TITLE}
            </h2>
          </div>

          {perfis.length > 0 && (
            /* Os ícones dos perfis oficiais são só desktop (`lg`+) por ora: abaixo
               disso a fileira empurra o carrossel para baixo da dobra e os mesmos
               perfis já estão no rodapé. */
            <ul
              className={`flex-wrap items-center gap-2 ${PERFIS_VISIBILIDADE}`}
            >
              {perfis.map((perfil) => (
                <li key={perfil.network}>
                  <MPFLink
                    href={perfil.url}
                    aria-label={`${perfil.label} do MPF`}
                    className={`${BLOCO} rounded-lg text-blue-900 transition-opacity hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:text-white`}
                  >
                    <SocialIcon
                      network={perfil.network}
                      variant="mono"
                      size={32}
                    />
                  </MPFLink>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Régua do cabeçalho, na mesma cor do título. */}
        <hr className="border-t border-[color:var(--orange-500)] dark:border-night-850" />
      </div>

      {/* Duas formas de DOM, não duas visibilidades do mesmo: no desktop os
          quatro pequenos são uma grade 2×2 ao lado do card alto de vídeo; no
          mobile os pequenos viram slides de carrossel e o vídeo fica solto
          abaixo dele. Quem liga e desliga é só CSS, sem JS medindo a tela.

          O corte é `lg` (992px), como na faixa "Iniciativas e Projetos". */}
      <div className="hidden gap-4 lg:grid lg:auto-rows-fr lg:grid-cols-3">
        {/* Grade 2+2+1. Dois grids aninhados, e não um de três colunas com
            `row-span`: o `row-span` só se comporta quando todas as trilhas
            têm altura previsível, e aqui a coluna dos pequenos cresce com o
            texto. Aninhado, o card alto simplesmente estica com `h-full`. */}
        <div className="grid grid-cols-2 gap-4 lg:col-span-2">
          {pequenos.map((card) => (
            <RedeCard
              key={card.slot}
              card={card}
              onEditItem={onEditItem}
              onPlay={setVideo}
            />
          ))}
        </div>

        {grande && (
          <RedeCard
            card={grande}
            onEditItem={onEditItem}
            onPlay={setVideo}
            tall
          />
        )}
      </div>

      {/* O 5º card (vídeo) NÃO entra no carrossel: fica solto, full-width,
          logo abaixo dos bullets — é o único item que não é curadoria de
          posts das redes, e assim se destaca como convidado a assistir. */}
      <div className="flex flex-col gap-4 lg:hidden">
        <AcompanheRedesCarouselMobile
          cards={pequenos}
          onEditItem={onEditItem}
          onPlay={setVideo}
        />

        {grande && (
          <RedeCard card={grande} onEditItem={onEditItem} onPlay={setVideo} />
        )}
      </div>

      <YoutubeDialog video={video} onClose={() => setVideo(null)} />
    </section>
  );
};

export default AcompanheRedesSection;
